import {
  GetAllDeliveryProfilesOutput,
  GetAllDeliveryProfilesResult,
} from "@redotech/admin-sdk/rpc/schema/shopify/get-all-delivery-profiles";
import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-02_filled.svg";
import {
  CoverageProductEnum,
  DeliveryFilterType,
  DeliverySortType,
} from "@redotech/redo-model/integration/redo-in-shipping/ris";
import {
  ShippingRateTable,
  ShippingRateTableArraySchema,
  SkippedZone,
  SkippedZoneReason,
  isFixedRate,
  isFixedRateTable,
} from "@redotech/redo-model/integration/redo-in-shipping/shipping-rate-table";
import {
  DeliveryLocationGroupZone,
  DeliveryMethodDefinition,
  ProfileLocationGroup,
} from "@redotech/redo-model/integration/shopify/delivery-profile";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckboxAdvanced } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-advanced";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import ThreeDotsHorizontalIcon from "@redotech/redo-web/icon-old/three-dots-horizontal.svg";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { PackageProtectionForm } from "@redotech/redo-web/settings-elements/package-protection-elements";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { getTextFromConditions } from "@redotech/redo-web/utils/shipping-rates";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { RisForm } from "../coverage-product";
import { EditRate } from "./edit-rate";

//RateTables don't have a good unique key so just use a counter for now
function counter() {
  let value = 0;
  return () => value++;
}

export const ShippingZones = memo(function ShippingZones({
  input,
  packageProtectionInput,
  hasReturnsCoverage,
  hasCarrierServices,
  teamId,
}: {
  input: RisForm;
  packageProtectionInput: PackageProtectionForm;
  hasReturnsCoverage: boolean;
  hasCarrierServices: boolean;
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [conversionError, setConversionError] = useState<string | undefined>();
  const [skippedZones, setSkippedZones] = useState<SkippedZone[]>([]);
  const [editRate, setEditRate] = useState<ShippingRateTable | undefined>();
  //Regardless if they hit refresh or not, we still want to load their delivery profiles (but don't show it on the UI until they click load)
  const [deliveryProfilesLoad, triggerDeliveryProfilesLoad] = useTriggerLoad(
    async () => {
      const result: GetAllDeliveryProfilesOutput =
        await rpcClient.getAllDeliveryProfiles({ teamId });
      switch (result.result) {
        case GetAllDeliveryProfilesResult.SUCCESS:
          return result.deliveryProfiles;
        case GetAllDeliveryProfilesResult.ERROR__GRAPHQL_ACCESS_DENIED:
          throw new Error(
            "In order to enable RIS, this merchant must update our collaborator access to include Shipping and Delivery permissions. Please SAVE (on this page) and tell the merchant to go to their Redo dashboard and click 'Update Permission' in the banner that shows.",
          );
        case GetAllDeliveryProfilesResult.NOT_FOUND:
          throw new Error(
            "No delivery profiles found for this team. Please create a delivery profile in Shopify and try again.",
          );
        default:
          assertNever(result);
      }
    },
  );

  useEffect(() => {
    triggerDeliveryProfilesLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rateTables = input.value?.rateTables;

  const packageProtectionEnabled =
    packageProtectionInput.value.enabled &&
    packageProtectionInput.value.coverage;
  const loadAllProfiles = useHandler(async () => {
    if (!deliveryProfilesLoad.value) {
      return;
    }

    setConversionError(undefined);
    setSkippedZones([]);

    try {
      const results = await Promise.all(
        deliveryProfilesLoad.value.map(async (profile) => {
          return await rpcClient.getRateTablesFromDeliveryProfile({
            deliveryProfile: profile,
            hasPackageProtection: packageProtectionEnabled,
            hasReturnCoverage: hasReturnsCoverage,
            hasCarrierServices,
            teamId,
          });
        }),
      );

      const allRateTables = results.flatMap((r) => r.rateTables);
      const allSkippedZones = results.flatMap((r) => r.skippedZones);

      input?.input?.inputs.rateTables.setValue(allRateTables);
      setSkippedZones(allSkippedZones);
      triggerDeliveryProfilesLoad();
    } catch (e) {
      setConversionError(e?.toString());
    }
  });

  if (!rateTables) {
    return null;
  }

  function getTitle(shippingRateLocation: ShippingRateTable) {
    return `${shippingRateLocation.destinationLocations[0].country} - Warehouse (${
      shippingRateLocation.originLocations[0].country +
      " " +
      shippingRateLocation.originLocations[0].provinces.join(",")
    })`;
  }

  function getCountryTitle(index: number, rateTables: ShippingRateTable[]) {
    const currentTitle = getTitle(rateTables[index]);

    if (index === 0) {
      return currentTitle;
    }

    const previousTitle = getTitle(rateTables[index - 1]);
    if (currentTitle === previousTitle) {
      return null;
    }
    return currentTitle;
  }

  function groupRateTablesByProfile(rateTables: ShippingRateTable[]) {
    const grouped = new Map<string, ShippingRateTable[]>();

    for (const rateTable of rateTables) {
      const profileKey = rateTable.deliveryProfile
        ? `${rateTable.deliveryProfile.id}|${rateTable.deliveryProfile.name}`
        : "no-profile";

      if (!grouped.has(profileKey)) {
        grouped.set(profileKey, []);
      }
      grouped.get(profileKey)!.push(rateTable);
    }

    return Array.from(grouped.entries()).map(([profileKey, tables]) => {
      const [profileId, profileName] =
        profileKey === "no-profile"
          ? [null, "Ungrouped rates"]
          : profileKey.split("|");

      return { profileId, profileName, rateTables: tables };
    });
  }

  return (
    <Card
      headerExtra={
        <Flex align="flex-start">
          <RedoButton
            disabled={deliveryProfilesLoad.pending === true}
            hierarchy="secondary"
            IconTrailing={RefreshIcon}
            onClick={loadAllProfiles}
            text={
              deliveryProfilesLoad.pending
                ? "Fetching"
                : rateTables.length > 0
                  ? "Refresh"
                  : "Load"
            }
          />
        </Flex>
      }
      subtitle="Select which rates have Redo Coverage included."
      title="Shipping Zones"
      titleBadge={
        hasCarrierServices ? (
          <RedoBadge color="success" text="Carrier services" />
        ) : undefined
      }
    >
      {deliveryProfilesLoad.error || conversionError ? (
        <Text fontSize="md" fontWeight="regular" textColor="error">
          {deliveryProfilesLoad.error?.message}
          {conversionError}
        </Text>
      ) : (
        <>
          {skippedZones.length > 0 && (
            <Flex
              dir="column"
              gap="xs"
              style={{
                backgroundColor: "var(--redo-colors-warning-50)",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <Text fontSize="sm" fontWeight="medium" textColor="primary">
                Some zones were skipped:
              </Text>
              {skippedZones.map((zone, index) => (
                <Text fontSize="sm" key={index} textColor="secondary">
                  • {zone.zoneName} ({zone.profileName}):{" "}
                  {zone.reason === SkippedZoneReason.INTERNATIONAL
                    ? "International zones are not supported"
                    : "No fixed shipping rates available (only carrier-calculated rates)"}
                </Text>
              ))}
            </Flex>
          )}
          {rateTables.length > 0 &&
            input.input?.inputs.enabled !== undefined && (
              <FormSwitch
                input={input.input.inputs.enabled}
                label="Enabled"
                onChange={function (_: boolean): void {
                  input.input?.inputs.coverage.setValue(
                    CoverageProductEnum.SHIPPING,
                  );
                }}
              />
            )}

          {groupRateTablesByProfile(rateTables.filter(isFixedRateTable)).map(
            (profileGroup) => (
              <Flex
                dir="column"
                gap="md"
                key={profileGroup.profileId || "no-profile"}
              >
                <Flex>
                  <Text fontSize="lg" fontWeight="medium" textColor="primary">
                    {profileGroup.profileName}
                  </Text>
                </Flex>
                {profileGroup.rateTables.map((rateTable, tableIndex) => {
                  const countryTitle = getCountryTitle(
                    tableIndex,
                    profileGroup.rateTables,
                  );
                  return (
                    <Flex dir="column" gap="xs" key={counter().toString()}>
                      {countryTitle && (
                        <Flex>
                          <Text fontSize="sm" textColor="secondary">
                            {countryTitle}
                          </Text>
                        </Flex>
                      )}
                      {rateTable.rates.map((rate, rateIndex) => (
                        <RedoCheckboxAdvanced
                          checked={rate.enabled === true}
                          key={counter().toString()}
                          onClick={function (): void {
                            const isEnabled = !rate.enabled;

                            const newRateTables =
                              ShippingRateTableArraySchema.safeParse(
                                rateTables.map((rateTableOld) => ({
                                  ...rateTableOld,
                                  rates: rateTableOld.rates.map(
                                    (rateOld, rIndex) => ({
                                      ...rateOld,
                                      //If they have carrier services just update single rate, if they are basic every rate must be the same
                                      enabled:
                                        rateTableOld.code === rateTable.code &&
                                        (rateIndex === rIndex ||
                                          !hasCarrierServices)
                                          ? isEnabled
                                          : rateOld.enabled,
                                    }),
                                  ),
                                })),
                              );

                            if (!newRateTables.success) {
                              console.error(
                                "Error parsing new rate tables",
                                newRateTables.error,
                              );
                              return;
                            }

                            input?.input?.inputs.rateTables.setValue(
                              newRateTables.data,
                            );
                          }}
                          rightContent={
                            <Flex gap="lg">
                              {isFixedRate(rate) && (
                                <RedoBadge
                                  color="warning"
                                  text={moneyFormat({
                                    amount: +rate.price,
                                    currency: rate.currency as Currency,
                                  })}
                                />
                              )}
                              {hasCarrierServices && (
                                <RedoButton
                                  hierarchy="secondary"
                                  IconLeading={ThreeDotsHorizontalIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditRate(rateTable);
                                  }}
                                />
                              )}
                            </Flex>
                          }
                          title={rateTable.name}
                        >
                          {isFixedRate(rate) &&
                            getTextFromConditions(rate.conditions).join("\n")}
                        </RedoCheckboxAdvanced>
                      ))}
                    </Flex>
                  );
                })}
              </Flex>
            ),
          )}

          {rateTables.length > 0 && input.input && (
            <>
              <FormSelectDropdown
                description="Sorting of shipping (If RIC is enabled, this will be ignored)"
                input={input.input?.inputs.deliverySortType}
                label="Shipping sorting"
                options={Object.values(DeliverySortType)}
              >
                {(s) => s}
              </FormSelectDropdown>
              <FormSelectDropdown
                description="Filtering of shipping"
                input={input.input?.inputs.deliveryFilterType}
                label="Shipping filtering"
                options={Object.values(DeliveryFilterType)}
              >
                {(s) => s}
              </FormSelectDropdown>
            </>
          )}
        </>
      )}

      {editRate && (
        <EditRate
          close={() => setEditRate(undefined)}
          rateTable={editRate}
          save={(newRateTable) => {
            //Make sure the names are unique in our rates
            const namesAreUnique =
              rateTables.filter(
                (rateTable) =>
                  rateTable.name === newRateTable.name &&
                  rateTable.code !== newRateTable.code,
              ).length === 0;
            if (!namesAreUnique) {
              return "Name already exists";
            }
            //Make sure names are unique in shopify across all profiles
            if (hasCarrierServices && deliveryProfilesLoad.value) {
              const allShopifyRateNames =
                deliveryProfilesLoad.value.flatMap((profile) =>
                  profile.profileLocationGroups
                    .flatMap(
                      (profileLocationGroup: ProfileLocationGroup) =>
                        profileLocationGroup.locationGroupZones,
                    )
                    .flatMap(
                      (locationGroupZone: DeliveryLocationGroupZone) =>
                        locationGroupZone.methodDefinitions,
                    )
                    .flatMap(
                      (methodDefinition: DeliveryMethodDefinition) =>
                        methodDefinition.name,
                    ),
                ) || [];

              if (allShopifyRateNames.includes(newRateTable.name)) {
                return "Name already exists in Shopify";
              }
            }
            const newRateTables = rateTables.map((rateTable) => {
              if (rateTable.code === newRateTable.code) {
                return newRateTable;
              }
              return rateTable;
            });
            input?.input?.inputs.rateTables.setValue(newRateTables);
            setEditRate(undefined);

            return undefined;
          }}
        />
      )}
    </Card>
  );
});
